/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { isEntityInvalid, Table } from '@gh/common-shared';
import { ID, WithDirty, WithFormMode, WithRecalculationNeeded } from '@gh/common-shared-data';
import { FormMode } from '@gh/core-ui';
import { FocusedCell } from '@gh/core-ui-grid';
import {
  QuoteHousePlanTop,
  QuoteHousePlanTopList,
  QuoteHousePlanTopListCc,
  quoteHousePlanTopTable,
} from '../../resources/quote.model';

export enum QuoteTopDialogStateEventType {
  NavigateToInvalid = 'NavigateToInvalid',
  Load = 'Load',
  SetTopValue = 'SetTopValue',
  CommitTop = 'CommitTop',
  ResetTop = 'ResetTop',
  ResetFocusedCell = 'ResetFocusedCell',
  Merge = 'Merge',
}

export type QuoteTopDialogStateLoadEvent = {
  type: QuoteTopDialogStateEventType.Load;
  priceBookId: ID;
  tops: QuoteHousePlanTopList;
  formMode: number;
};
export type QuoteTopDialogStateMergeEvent = { type: QuoteTopDialogStateEventType.Merge; tops: QuoteHousePlanTopList };

export type QuoteTopDialogStateSetTopValueEvent = {
  type: QuoteTopDialogStateEventType.SetTopValue;
  topId: ID;
  value: number;
};

export type QuoteTopDialogStateCommitTopEvent = {
  type: QuoteTopDialogStateEventType.CommitTop;
  topId: ID;
};

export type QuoteTopDialogStateResetTopEvent = {
  type: QuoteTopDialogStateEventType.ResetTop;
  top: QuoteHousePlanTop;
};

export type QuoteTopDialogStateResetFocusedCellEvent = { type: QuoteTopDialogStateEventType.ResetFocusedCell };
export type QuoteTopDialogStateNavigateToInvalidEvent = { type: QuoteTopDialogStateEventType.NavigateToInvalid };

export type QuoteTopDialogStateEvent = QuoteTopDialogStateLoadEvent | QuoteTopDialogStateMergeEvent
  | QuoteTopDialogStateSetTopValueEvent
  | QuoteTopDialogStateCommitTopEvent | QuoteTopDialogStateResetTopEvent
  | QuoteTopDialogStateResetFocusedCellEvent | QuoteTopDialogStateNavigateToInvalidEvent;

export type QuoteTopDialogState = WithFormMode<WithDirty<WithRecalculationNeeded<{
  priceBookId: ID;
  costCenters: QuoteHousePlanTopListCc[];
  tops: Table<QuoteHousePlanTop>;
  selection: { focusedCell?: FocusedCell };
}>>>;

export const QUOTE_TOP_DIALOG_INIT_STATE: QuoteTopDialogState = {
  formMode: <any>void 0,
  priceBookId: <any>void 0,
  costCenters: [],
  tops: quoteHousePlanTopTable.create(),
  isRecalculationNeeded: false,
  isDirty: false,
  selection: {},
};

export const queryQuoteTops = (state: QuoteTopDialogState) => quoteHousePlanTopTable.byOrder(state.tops, 'code');
export const queryQuoteTop = (state: QuoteTopDialogState, topId: ID) => quoteHousePlanTopTable.byId(state.tops, topId);
export const queryQuoteTopsFocusedCell = (state: QuoteTopDialogState) => state.selection.focusedCell;
export const queryQuoteTopsInvalid = (state: QuoteTopDialogState) => queryQuoteTops(state).filter(isEntityInvalid);
export const queryQuoteTopsHasErrors = (state: QuoteTopDialogState) => queryQuoteTopsInvalid(state).length > 0;
export const queryQuoteTopsSaveModel = (state: QuoteTopDialogState) => <QuoteHousePlanTopList>({
  priceBookId: state.priceBookId,
  costCenters: state.costCenters,
  takeOffParams: queryQuoteTops(state),
});
